
const cdnUrl = 'https://cdn.statusas.com'

/**
 * Gets the standardised manufacturer name from a make string
 * @param path The make string (e.g. "Toyota Hilux 2023")
 * @returns The standardised manufacturer name (e.g. "toyota")
 */
export const getManufacturerName = (path: string): string => {
    let manu = ''
    if (path.indexOf(' ') != -1) {
        manu = path.substring(0, path.indexOf(' '))

        // If year is written first, skip it
        if (parseInt(manu)) {
            manu = path.substring(5, path.indexOf(' ', 5))
        }
    } else {
        manu = path
    }
    return manu.toLowerCase()
}

/**
 * Gets the path to the manufacturer icon
 * @param manufacturerName The standardised manufacturer name
 * @param size The icon size
 * @returns The URL to the manufacturer icon
 */
export const getManufacturerIconPath = (manufacturerName: string, size: number): string => {
    let cdnSize = 48
    if (size > 0 && size <= 16) cdnSize = 16
    if (size > 16 && size < 32) cdnSize = 32
    
    return `${cdnUrl}/ManufacturerIcons/${cdnSize}/${manufacturerName}.png`
}

/**
 * Checks if a manufacturer icon is available
 * @param make The make string
 * @param size The icon size
 * @returns A promise that resolves to true if the icon is available, false otherwise
 */
export const isManufacturerIconAvailable = async (make: string, size: number): Promise<boolean> => {
    const manufacturerName = getManufacturerName(make)
    const iconPath = getManufacturerIconPath(manufacturerName, size)
    
    return new Promise<boolean>((resolve) => {
        const img = new Image()
        img.onload = () => resolve(true)
        img.onerror = () => resolve(false)
        img.src = iconPath
    })
}

// Cache to store icon availability results
const iconAvailabilityCache = new Map<string, boolean>()

/**
 * Gets the availability of a manufacturer icon from cache or checks it
 * @param make The make string
 * @param size The icon size
 * @param callback Optional callback to be called when availability is determined
 * @returns A boolean indicating if the icon is available (if known from cache), or null if not yet known
 */
export const getManufacturerIconAvailability = (
    make: string, 
    size: number, 
    callback?: (available: boolean) => void
): boolean | null => {
    const manufacturerName = getManufacturerName(make)
    const cacheKey = `${manufacturerName}-${size}`
    
    // Check cache first
    if (iconAvailabilityCache.has(cacheKey)) {
        const isAvailable = iconAvailabilityCache.get(cacheKey)
        if (callback) callback(isAvailable!)
        return isAvailable!
    }
    
    // Not in cache, check availability asynchronously
    isManufacturerIconAvailable(make, size)
        .then(available => {
            iconAvailabilityCache.set(cacheKey, available)
            if (callback) callback(available)
        })
    
    return null // Not known yet
}
